import { Injectable, OnDestroy } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Observable, Subject, interval, of, timer } from 'rxjs';
import { filter, map, take, takeUntil, switchMap, debounceTime, tap, distinctUntilChanged, retry, skip } from 'rxjs/operators';
import { SessionResponseModel } from '../models/sessionResponseModel'
import { LiveServiceHttpService } from './live-service-http.service';
import { ToastService } from './toast-service.service';
import { HospitalTheatreCaseModel } from '../models/hospitalTheatreCaseModel';
import { APP_CONFIG } from '../configs/common.config';
import * as Sentry from "@sentry/angular";

const MAX_RETRY_ATTEMPTS = 10; // can only try refresh 10 times before critical failure
const MAX_PING_DELAY = 3000; // 5s between ping and expecting a pong max

interface WebSocketMessage {
  event: string
}

@Injectable({
  providedIn: 'root'
})
export class WsLiveServiceService implements OnDestroy {


  private status$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public status: Observable<boolean> = this.status$.asObservable();
  private socket$: WebSocketSubject<any> | null = null;
  private pingInterval$!: Observable<number>;
  private destroyPing$ = new Subject<void>();
  private expectPong: boolean = false;
  private socketUrl: string | null = null;
  private liveStreamType: string = '';
  private refreshIn: number | null = null;
  private expiresIn: number | null = null; // Currently unused, for future use where needed

  constructor(
    private liveServiceHttpService: LiveServiceHttpService,
    private toastService: ToastService
  ) {
  }

  ngOnDestroy(): void {
    this.destroyPing$.next();
    this.destroyPing$.complete();
    if (this.socket$ && !this.socket$.closed) {
      this.socket$.complete();
      this.socket$.unsubscribe();
    }
  }

  setNewConnection(session: SessionResponseModel): Observable<{ liveStreamType: string }> {
    this.socketUrl = session.ws_url;
    /* 
      Both session.expires_at and session.refresh_at are NumericDate's (seconds since Epoch),
        need to be converted so a duration we can use in timers for refresh logic
    */
    this.expiresIn = (session.expires_at * 1000) - new Date().valueOf();
    this.refreshIn = ((session.refresh_at * 1000) - new Date().valueOf()) + (this.expiresIn / 2); // refresh at midpoint between nbf and exp claims to ensure valid refresh attempt 
    if (session.live_feed_type) {
      this.liveStreamType = session.live_feed_type;
    }

    return this.connect();
  }

  public connect(): Observable<{ liveStreamType: string }> {
    if (!this.socketUrl) {
      throw new Error('WebSocket connection url has not been set.');
    }
    const onConnect = new Subject<Event>();
    onConnect.pipe(map((_) => true)).subscribe(this.status$);
    const onDisconnect = new Subject<CloseEvent>();
    onDisconnect.pipe(map((_) => false)).subscribe(this.status$);

    // @TODO we can change this function to no longer be an observable as we have a status that can be subbed to from the outside to get response

    this.socket$ = webSocket({
      url: this.socketUrl,
      openObserver: onConnect,
      closeObserver: onDisconnect
    });
    this.socket$.pipe(
      retry({
        delay: err => {
          this.status$.next(false);
          this.toastService.displayInfo('Live feed connection dropped, trying to reconnect');
          return timer(APP_CONFIG.RECONNECT_INTERVAL);
        }
      })
    ).subscribe();

    return onConnect.pipe(
      switchMap(res => {
        this.setPingInterval();
        this.setRefreshTimer();
        return of({ liveStreamType: this.liveStreamType });
      })
    );
  }

  public disconnect() {
    if (this.socket$) {
      if (!this.socket$.closed) this.socket$.complete();
      this.socket$.unsubscribe();
    }
    this.destroyPing$.next();
    this.socket$ = null;
  }

  // Used to fetch the initial live feed fetch
  public emitFetchLiveFeedEvent() {
    if (!this.socket$) {
      throw new Error('WebSocket connection is not initialized.');
    }
    const message: WebSocketMessage = { event: 'fetchLiveFeed' };
    this.socket$.next(message);
  }

  public onEvent(event: string): Observable<HospitalTheatreCaseModel[]> {
    if (!this.socket$) {
      throw new Error('WebSocket connection is not initialized.');
    }
    return this.socket$.pipe(
      filter((response) => {
        return response.event === event;
      }),
      map((response) => response.payload as HospitalTheatreCaseModel[]),
    );
  }

  public onLiveFeedFetchEvent(): Observable<HospitalTheatreCaseModel[]> {
    return this.onEvent('fetch-live-feed');
  }

  public onLiveFeedUpdateEvent(): Observable<HospitalTheatreCaseModel[]> {
    return this.onEvent('live-feed-update');
  }

  public onPongEvent(): Observable<{ message: string }> {
    if (!this.socket$) {
      throw new Error('WebSocket connection is not initialized.');
    }
    return this.socket$.pipe(
      filter((response) => {
        return response.event === 'pong';
      }),
      map((response) => response.payload as { message: string }),
    );
  }

  public get connectionStatus$(): Observable<boolean> {
    return this.status$.pipe(distinctUntilChanged());
  }

  private ping() {
    if (!this.socket$) {
      throw new Error('WebSocket connection is not initialized.');
    }
    this.expectPong = true;
    const message: WebSocketMessage = { event: 'ping' };
    this.socket$.next(message);;
  }

  private setPingInterval(): void {
    this.pingInterval$ = interval(APP_CONFIG.WS_PING_INTERVAL);
    this.pingInterval$.pipe(
      takeUntil(this.destroyPing$),
      debounceTime(MAX_PING_DELAY),
      tap(() => {
        if (this.expectPong) {
          // If we fail to get response try reconnect by updating status
          this.disconnect();
          this.connect().subscribe();
        } else {
          this.ping();
        }
      }),
      switchMap(res => this.onPongEvent()),
    ).subscribe({
      next: res => {
        if (res && res.message === 'pong') {
          this.expectPong = false;
        }
      }
    });
  }

  private setRefreshTimer() {
    if (!this.refreshIn) {
      return;
    }
    setTimeout(() => {
      // Check if active live feed session
      this.refreshSession();
    }, this.refreshIn);
  }

  // @TODO Move retry logic to debounce on pipe
  private refreshSession(retry: number = 1) {
    this.liveServiceHttpService.refreshToken().pipe(take(1))
      .subscribe({
        next: async res => {
          this.disconnect();
          // Set new connection with updates
          this.setNewConnection(res).subscribe();
        },
        error: cause => {
          // Failed to refresh, retry up to 3 times
          if (retry <= MAX_RETRY_ATTEMPTS) {
            this.toastService.displayError('Failed to refresh connection, retrying');
            setTimeout(() => {
              this.refreshSession(++retry);
            }, 1000);
          } else {
            this.toastService.displayError('Failed to refresh connection');
            Sentry.captureException(new Error('Failed To Refresh Connection', { cause }));
            location.reload();
          }
        }
      });
  }
}
